import { Attachment } from '@/modules/Upload/types'
import {
    createContext,
    useContext,
    useState,
    useEffect,
    useCallback,
    useRef,
} from 'react'
import { FilePreviewDialog } from 'vnetwork-ui'
import { PresignedGetUrl } from '@/modules/Drive/services'
import toast from 'react-hot-toast'
import { t } from 'i18next'
import datacentral from '@/utils/axios/datacentral'
import { useQueryClient } from '@tanstack/react-query'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'

interface FileItem {
    url?: string
    fileKey?: string
    downloadUrl?: string
    attachmentInfo?: Partial<Attachment>
    manualType?: boolean
    extension?: string
    isHeic?: boolean
}

interface FilePreviewContextProps {
    preview: (
        url: string,
        downloadUrl?: string,
        attachmentInfo?: Partial<Attachment>,
        manualType?: boolean
    ) => void
    previewMultiple: (files: FileItem[], startIndex?: number) => void
    closePreview: () => void
    goToNext: () => void
    goToPrevious: () => void
    currentIndex: number
    totalFiles: number
    hasNext: boolean
    hasPrevious: boolean
    isLoading: boolean
}

const FilePreviewContext = createContext<FilePreviewContextProps>({
    preview: () => {},
    previewMultiple: () => {},
    closePreview: () => {},
    goToNext: () => {},
    goToPrevious: () => {},
    currentIndex: 0,
    totalFiles: 0,
    hasNext: false,
    hasPrevious: false,
    isLoading: false,
})

const OFFICE_EXTENSIONS = ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx']

const FilePreviewContextProvider: React.FC<{ children: React.ReactNode }> = ({
    children,
}) => {
    const [files, setFiles] = useState<FileItem[]>([])
    const [currentIndex, setCurrentIndex] = useState(0)
    const [open, setOpen] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const heicUrlsRef = useRef<Map<string, string>>(new Map())
    const queryClient = useQueryClient()

    const currentFile = files[currentIndex]
    const totalFiles = files.length
    const hasNext = currentIndex < totalFiles - 1
    const hasPrevious = currentIndex > 0

    const generatePresignedUrl = useCallback(
        async (fileItem: FileItem): Promise<string> => {
            if (fileItem.url) return fileItem.url
            if (!fileItem.fileKey) throw new Error('No file key provided')

            try {
                setIsLoading(true)
                const response = await PresignedGetUrl(fileItem.fileKey)
                let url = response.result

                if (
                    fileItem.extension &&
                    OFFICE_EXTENSIONS.some((ext) =>
                        fileItem.extension!.includes(ext)
                    )
                ) {
                    url = encodeURIComponent(url)
                }

                return url
            } catch (error) {
                console.error('Failed to generate presigned URL:', error)
                toast.error(t('try-again'))
                throw error
            } finally {
                setIsLoading(false)
            }
        },
        []
    )

    const updateFileWithUrl = useCallback(
        async (index: number) => {
            setFiles((prevFiles) => {
                if (index < 0 || index >= prevFiles.length) return prevFiles

                const fileItem = prevFiles[index]
                if (fileItem.url) return prevFiles

                // Handle HEIC files differently
                if (fileItem.isHeic && fileItem.fileKey) {
                    setIsLoading(true)

                    const fetchHeicPreview = async () => {
                        // Try to get from cache first
                        const cachedData = queryClient.getQueryData<Blob>([
                            QUERY_KEYS.HEIC_PREVIEW,
                            fileItem.fileKey,
                        ])

                        if (cachedData) {
                            // Use cached data
                            return cachedData
                        } else {
                            // Fetch and cache if not present
                            return queryClient.fetchQuery({
                                queryKey: [
                                    QUERY_KEYS.HEIC_PREVIEW,
                                    fileItem.fileKey,
                                ],
                                queryFn: async () => {
                                    const response = await datacentral.get(
                                        '/drive/heic-preview',
                                        {
                                            params: { path: fileItem.fileKey },
                                            responseType: 'blob',
                                        }
                                    )
                                    return response.data as Blob
                                },
                                staleTime: 5 * 60 * 1000, // 5 minutes
                            })
                        }
                    }

                    // Fetch both the converted preview and original URL concurrently
                    Promise.all([
                        fetchHeicPreview(),
                        PresignedGetUrl(fileItem.fileKey),
                    ])
                        .then(([blob, presignedResponse]) => {
                            const previewUrl = URL.createObjectURL(blob)
                            const downloadUrl = presignedResponse.result

                            // Store the preview URL for cleanup
                            if (fileItem.fileKey) {
                                const oldUrl = heicUrlsRef.current.get(
                                    fileItem.fileKey
                                )
                                if (oldUrl) {
                                    URL.revokeObjectURL(oldUrl)
                                }
                                heicUrlsRef.current.set(
                                    fileItem.fileKey,
                                    previewUrl
                                )
                            }

                            setFiles((currentFiles) =>
                                currentFiles.map((file, i) =>
                                    i === index
                                        ? {
                                              ...file,
                                              url: previewUrl,
                                              downloadUrl: downloadUrl,
                                              attachmentInfo: {
                                                  ...file.attachmentInfo,
                                                  attachment_file_url:
                                                      previewUrl,
                                                  attachment_file_type:
                                                      'image/jpeg',
                                              },
                                          }
                                        : file
                                )
                            )
                            setIsLoading(false)
                        })
                        .catch((error) => {
                            console.error('Failed to load HEIC preview:', error)
                            toast.error(t('Failed to load HEIC preview'))
                            setIsLoading(false)
                        })
                } else {
                    // Handle non-HEIC files (existing logic)
                    generatePresignedUrl(fileItem)
                        .then((url) => {
                            setFiles((currentFiles) =>
                                currentFiles.map((file, i) =>
                                    i === index
                                        ? {
                                              ...file,
                                              url,
                                              downloadUrl: url, // Same URL for both
                                              attachmentInfo: {
                                                  ...file.attachmentInfo,
                                                  attachment_file_url: url,
                                              },
                                          }
                                        : file
                                )
                            )
                        })
                        .catch((error) =>
                            console.error(
                                'Failed to update file with URL:',
                                error
                            )
                        )
                }

                return prevFiles
            })
        },
        [generatePresignedUrl]
    )

    const preview = useCallback(
        (
            url: string,
            downloadUrl?: string,
            attachmentInfo?: Partial<Attachment>,
            manualType?: boolean
        ) => {
            setFiles([
                {
                    url,
                    downloadUrl,
                    attachmentInfo,
                    manualType: manualType || false,
                },
            ])
            setCurrentIndex(0)
            setOpen(true)
        },
        []
    )

    const previewMultiple = useCallback(
        (fileItems: FileItem[], startIndex = 0) => {
            const startIdx = Math.min(startIndex, fileItems.length - 1)
            setFiles(fileItems)
            setCurrentIndex(startIdx)
            setOpen(true)

            if (fileItems.length > 0) {
                setTimeout(() => updateFileWithUrl(startIdx), 0)

                // Pre-fetch HEIC previews for nearby files
                fileItems.forEach((item, idx) => {
                    if (
                        item.isHeic &&
                        item.fileKey &&
                        Math.abs(idx - startIdx) <= 2
                    ) {
                        // Pre-fetch HEIC files within 2 positions of current
                        queryClient.prefetchQuery({
                            queryKey: [QUERY_KEYS.HEIC_PREVIEW, item.fileKey],
                            queryFn: async () => {
                                const response = await datacentral.get(
                                    '/drive/heic-preview',
                                    {
                                        params: { path: item.fileKey },
                                        responseType: 'blob',
                                    }
                                )
                                return response.data as Blob
                            },
                            staleTime: 5 * 60 * 1000, // 5 minutes
                        })
                    }
                })
            }
        },
        [updateFileWithUrl, queryClient]
    )

    const closePreview = useCallback(() => {
        setOpen(false)
        setFiles([])
        setCurrentIndex(0)
        setIsLoading(false)

        // Cleanup all HEIC preview URLs
        heicUrlsRef.current.forEach((url) => {
            URL.revokeObjectURL(url)
        })
        heicUrlsRef.current.clear()
    }, [])

    const goToNext = useCallback(() => {
        if (hasNext) {
            const nextIndex = currentIndex + 1
            setCurrentIndex(nextIndex)
            updateFileWithUrl(nextIndex)

            // Pre-fetch next HEIC file if exists
            const nextNextIndex = nextIndex + 1
            if (nextNextIndex < files.length) {
                const nextFile = files[nextNextIndex]
                if (nextFile.isHeic && nextFile.fileKey) {
                    queryClient.prefetchQuery({
                        queryKey: [QUERY_KEYS.HEIC_PREVIEW, nextFile.fileKey],
                        queryFn: async () => {
                            const response = await datacentral.get(
                                '/drive/heic-preview',
                                {
                                    params: { path: nextFile.fileKey },
                                    responseType: 'blob',
                                }
                            )
                            return response.data as Blob
                        },
                        staleTime: 5 * 60 * 1000,
                    })
                }
            }
        }
    }, [hasNext, currentIndex, updateFileWithUrl, files, queryClient])

    const goToPrevious = useCallback(() => {
        if (hasPrevious) {
            const prevIndex = currentIndex - 1
            setCurrentIndex(prevIndex)
            updateFileWithUrl(prevIndex)

            // Pre-fetch previous HEIC file if exists
            const prevPrevIndex = prevIndex - 1
            if (prevPrevIndex >= 0) {
                const prevFile = files[prevPrevIndex]
                if (prevFile.isHeic && prevFile.fileKey) {
                    queryClient.prefetchQuery({
                        queryKey: [QUERY_KEYS.HEIC_PREVIEW, prevFile.fileKey],
                        queryFn: async () => {
                            const response = await datacentral.get(
                                '/drive/heic-preview',
                                {
                                    params: { path: prevFile.fileKey },
                                    responseType: 'blob',
                                }
                            )
                            return response.data as Blob
                        },
                        staleTime: 5 * 60 * 1000,
                    })
                }
            }
        }
    }, [hasPrevious, currentIndex, updateFileWithUrl, files, queryClient])

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (!open) return

            switch (event.key) {
                case 'ArrowRight':
                    event.preventDefault()
                    goToNext()
                    break
                case 'ArrowLeft':
                    event.preventDefault()
                    goToPrevious()
                    break
                case 'Escape':
                    event.preventDefault()
                    closePreview()
                    break
            }
        }

        document.addEventListener('keydown', handleKeyDown)
        return () => document.removeEventListener('keydown', handleKeyDown)
    }, [open, goToNext, goToPrevious, closePreview])

    // Cleanup HEIC URLs on unmount
    useEffect(() => {
        return () => {
            heicUrlsRef.current.forEach((url) => {
                URL.revokeObjectURL(url)
            })
            heicUrlsRef.current.clear()
        }
    }, [])

    return (
        <FilePreviewContext.Provider
            value={{
                preview,
                previewMultiple,
                closePreview,
                goToNext,
                goToPrevious,
                currentIndex,
                totalFiles,
                hasNext,
                hasPrevious,
                isLoading,
            }}
        >
            {children}
            {open && currentFile && currentFile.url && (
                <div
                    className="relative"
                    onContextMenu={(e) => {
                        const isImage =
                            /\.(jpe?g|png|gif|bmp|webp|svg|mp4|mp3|jpg)$/i.test(
                                currentFile.url!
                            )
                        if (isImage) e.preventDefault()
                    }}
                >
                    <FilePreviewDialog
                        onClose={closePreview}
                        url={currentFile.url}
                        download={!!currentFile.downloadUrl || false}
                        attachmentInfo={
                            currentFile.attachmentInfo as Attachment
                        }
                        open={open}
                        manualType={currentFile.manualType}
                    />
                    {totalFiles > 1 && <FilePreviewNavigation />}
                </div>
            )}
            {open && isLoading && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white p-4 rounded-lg">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto" />
                        <p className="mt-2 text-sm text-gray-600">
                            Loading file...
                        </p>
                    </div>
                </div>
            )}
        </FilePreviewContext.Provider>
    )
}

export const useFilePreview = () => useContext(FilePreviewContext)
export default FilePreviewContextProvider

interface NavigationOverlayProps {
    direction: 'left' | 'right'
    onClick: () => void
    isEnabled: boolean
}

const NavigationOverlay: React.FC<NavigationOverlayProps> = ({
    direction,
    onClick,
    isEnabled,
}) => {
    const positionClass = direction === 'left' ? 'left-10' : 'right-10'
    const iconPath = direction === 'left' ? 'M15 19l-7-7 7-7' : 'M9 5l7 7-7 7'

    return (
        <div
            className={`fixed top-1/2 -translate-y-1/2 ${positionClass} w-20 z-[9999999] cursor-pointer flex items-center justify-center transition-all duration-300 hover:scale-125 ${
                isEnabled ? 'opacity-100' : 'opacity-30 pointer-events-none'
            }`}
            onClick={isEnabled ? onClick : undefined}
        >
            <div className="w-16 h-16 rounded-full flex items-center justify-center transition-all duration-200 group backdrop-blur-sm border-2 border-white/30 bg-gradient-to-br from-white/20 via-white/10 to-white/5 hover:from-white/30 hover:via-white/20 hover:to-white/10 hover:border-white/50 shadow-lg hover:shadow-xl">
                <svg
                    className="w-8 h-8 text-white drop-shadow-lg group-hover:scale-110 transition-transform duration-200"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2.5}
                        d={iconPath}
                    />
                </svg>
            </div>
        </div>
    )
}

const FilePreviewNavigation: React.FC = () => {
    const { goToNext, goToPrevious, hasNext, hasPrevious } = useFilePreview()

    return (
        <>
            <NavigationOverlay
                direction="left"
                onClick={goToPrevious}
                isEnabled={hasPrevious}
            />
            <NavigationOverlay
                direction="right"
                onClick={goToNext}
                isEnabled={hasNext}
            />
        </>
    )
}
